import { useMemo, useRef, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { formatMoneyUSD } from '../../../utils/functions';

export interface IndustryDimensionRow {
  key: string;
  kilos: number;
  othersKilos: number;
  totalValue: number;
  totalDifference: number;
  pctVsTarget: number | null;
  kilosAboveTargetPct: number | null;
  q1Pct: number | null;
  q2Pct: number | null;
  q3Pct: number | null;
  q4Pct: number | null;
}

type SortColumn =
  | 'key'
  | 'kilos'
  | 'othersKilos'
  | 'totalDifference'
  | 'perKiloDifference'
  | 'pctVsTarget'
  | 'kilosAboveTargetPct'
  | 'q1Pct'
  | 'q2Pct'
  | 'q3Pct'
  | 'q4Pct';

interface Props {
  title: string;
  dimensionLabel: string;
  rows: IndustryDimensionRow[];
  comparisonTarget: 'median' | 'q1' | 'q3';
}

const IndustryDimensionAnalysis = ({
  title,
  dimensionLabel,
  rows,
  comparisonTarget,
}: Props) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const [displayMode, setDisplayMode] = useState<'value' | 'percentage'>(
    'value'
  );
  const [sortColumn, setSortColumn] =
    useState<SortColumn>('totalDifference');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const chartRef = useRef<HTMLDivElement>(null);
  const targetLabel =
    comparisonTarget === 'median' ? 'Med.' : comparisonTarget.toUpperCase();

  const chartRows = useMemo(
    () => [...rows].sort((a, b) => b.totalDifference - a.totalDifference),
    [rows]
  );

  const sortedTableRows = useMemo(() => {
    const valueFor = (row: IndustryDimensionRow): string | number => {
      if (sortColumn === 'perKiloDifference') {
        return row.kilos > 0 ? row.totalDifference / row.kilos : 0;
      }
      return row[sortColumn] ?? -Infinity;
    };

    return [...rows].sort((a, b) => {
      const aValue = valueFor(a);
      const bValue = valueFor(b);
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc'
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
  }, [rows, sortColumn, sortDirection]);

  const totals = useMemo(() => {
    const totals = rows.reduce(
      (accumulator, row) => ({
        kilos: accumulator.kilos + row.kilos,
        othersKilos: accumulator.othersKilos + row.othersKilos,
        totalDifference:
          accumulator.totalDifference + row.totalDifference,
      }),
      { kilos: 0, othersKilos: 0, totalDifference: 0 }
    );
    return {
      ...totals,
      perKiloDifference:
        totals.kilos > 0 ? totals.totalDifference / totals.kilos : 0,
    };
  }, [rows]);

  const baseChartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'bar',
        height: 350,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 4,
          colors: {
            ranges: [
              { from: -Infinity, to: 0, color: '#f87171' },
              { from: 0, to: Infinity, color: '#4ade80' },
            ],
          },
        },
      },
      annotations: {
        yaxis: [{ y: 0, borderColor: '#ef4444', strokeDashArray: 0 }],
      },
      grid: {
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      xaxis: {
        categories: chartRows.map((row) => row.key),
        title: { text: dimensionLabel },
        labels: { rotate: -45, style: { fontSize: '12px' } },
      },
    }),
    [chartRows, darkMode, dimensionLabel]
  );

  const chartOptions = useMemo<ApexOptions>(() => {
    const isPercentage = displayMode === 'percentage';
    return {
      ...baseChartOptions,
      dataLabels: {
        enabled: true,
        formatter: (value: number) =>
          isPercentage
            ? `${Math.abs(Number(value)).toFixed(2)}%`
            : `$${Math.abs(Number(value)).toLocaleString('es-CL', {
                maximumFractionDigits: 0,
              })}`,
        style: { colors: [darkMode ? '#fff' : '#000'] },
      },
      yaxis: {
        title: {
          text: isPercentage
            ? t('Diferencia (%)')
            : t('Diferencia Total (USD)'),
        },
        labels: {
          formatter: (value: number) =>
            isPercentage
              ? `${Number(value).toFixed(2)}%`
              : `$${Number(value).toLocaleString('es-CL', {
                  maximumFractionDigits: 0,
                })}`,
        },
        tickAmount: isPercentage ? 10 : undefined,
        forceNiceScale: isPercentage,
      },
      tooltip: {
        y: {
          formatter: (value: number) =>
            isPercentage
              ? `${Number(value).toFixed(2)}%`
              : formatMoneyUSD(value, { decimals: 0 }),
        },
      },
    };
  }, [baseChartOptions, darkMode, displayMode, t]);

  const chartSeries = useMemo(
    () => [
      {
        name:
          displayMode === 'percentage'
            ? t('Diferencia (%)')
            : t('Diferencia Total'),
        data: chartRows.map((row) =>
          displayMode === 'percentage'
            ? row.totalValue > 0
              ? (row.totalDifference / row.totalValue) * 100
              : 0
            : row.totalDifference
        ),
      },
    ],
    [chartRows, displayMode, t]
  );

  const differenceClass = (value: number) =>
    value >= 0 ? 'text-success-500' : 'text-danger-500';

  return (
    <Card className='w-full md:p-4'>
      <CardHeader>
        <div className='flex flex-col sm:flex-row justify-between w-full items-center gap-4'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold text-center sm:text-left'>
              {title}
            </h3>
            {viewMode === 'chart' && (
              <ChartDownloadButton chartRef={chartRef} title={title} />
            )}
          </div>
          <div className='flex gap-4 w-full sm:w-auto items-center'>
            <Tabs
              size='sm'
              selectedKey={viewMode}
              onSelectionChange={(key) =>
                setViewMode(key as 'chart' | 'table')
              }
            >
              <Tab key='chart' title={t('Gráfico')} />
              <Tab key='table' title={t('Tabla')} />
            </Tabs>
            {viewMode === 'chart' && (
              <Select
                label={t('Tipo de Valor')}
                selectedKeys={[displayMode]}
                onSelectionChange={(keys) =>
                  setDisplayMode(
                    Array.from(keys)[0] as 'value' | 'percentage'
                  )
                }
                size='sm'
                className='w-full sm:min-w-40'
              >
                <SelectItem key='value' value='value'>
                  {t('Valor USD')}
                </SelectItem>
                <SelectItem key='percentage' value='percentage'>
                  {t('Porcentaje')}
                </SelectItem>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {viewMode === 'chart' ? (
          <div ref={chartRef}>
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type='bar'
              height={350}
            />
          </div>
        ) : (
          <Table
            removeWrapper
            aria-label={title}
            sortDescriptor={{
              column: sortColumn,
              direction:
                sortDirection === 'asc' ? 'ascending' : 'descending',
            }}
            onSortChange={(descriptor) => {
              setSortColumn(descriptor.column as SortColumn);
              setSortDirection(
                descriptor.direction === 'ascending' ? 'asc' : 'desc'
              );
            }}
          >
            <TableHeader>
              <TableColumn key='key' allowsSorting>
                {dimensionLabel}
              </TableColumn>
              <TableColumn key='kilos' allowsSorting>
                {t('Kg Principal')}
              </TableColumn>
              <TableColumn key='othersKilos' allowsSorting>
                {t('Kg Otros')}
              </TableColumn>
              <TableColumn key='totalDifference' allowsSorting>
                {t('Diferencia Total')}
              </TableColumn>
              <TableColumn key='perKiloDifference' allowsSorting>
                {t('Diferencia')} / {t('Kg Principal')}
              </TableColumn>
              <TableColumn key='pctVsTarget' allowsSorting>
                % vs {targetLabel}
              </TableColumn>
              <TableColumn key='kilosAboveTargetPct' allowsSorting>
                KG &gt; {targetLabel}
              </TableColumn>
              <TableColumn key='q1Pct' allowsSorting>
                Q1
              </TableColumn>
              <TableColumn key='q2Pct' allowsSorting>
                Q2
              </TableColumn>
              <TableColumn key='q3Pct' allowsSorting>
                Q3
              </TableColumn>
              <TableColumn key='q4Pct' allowsSorting>
                Q4
              </TableColumn>
            </TableHeader>
            <TableBody>
              {[
                <TableRow
                  key='total'
                  className='bg-default-50'
                >
                  <TableCell className='font-bold'>{t('TOTAL')}</TableCell>
                  <TableCell className='font-bold'>
                    {Math.round(totals.kilos).toLocaleString()}
                  </TableCell>
                  <TableCell className='font-bold'>
                    {Math.round(totals.othersKilos).toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={`font-bold ${differenceClass(
                      totals.totalDifference
                    )}`}
                  >
                    {formatMoneyUSD(totals.totalDifference, { decimals: 0 })}
                  </TableCell>
                  <TableCell
                    className={`font-bold ${differenceClass(
                      totals.perKiloDifference
                    )}`}
                  >
                    {formatMoneyUSD(totals.perKiloDifference, { decimals: 2 })}
                  </TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                  <TableCell className='font-bold'>-</TableCell>
                </TableRow>,
                ...sortedTableRows.map((row) => {
                  const perKiloDifference =
                    row.kilos > 0 ? row.totalDifference / row.kilos : 0;
                  return (
                    <TableRow key={row.key}>
                      <TableCell>{row.key}</TableCell>
                      <TableCell>
                        {Math.round(row.kilos).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {Math.round(row.othersKilos).toLocaleString()}
                      </TableCell>
                      <TableCell
                        className={differenceClass(row.totalDifference)}
                      >
                        {formatMoneyUSD(row.totalDifference, { decimals: 0 })}
                      </TableCell>
                      <TableCell
                        className={differenceClass(perKiloDifference)}
                      >
                        {formatMoneyUSD(perKiloDifference, { decimals: 2 })}
                      </TableCell>
                      <TableCell
                        className={
                          row.pctVsTarget == null
                            ? ''
                            : differenceClass(row.pctVsTarget)
                        }
                      >
                        {row.pctVsTarget == null
                          ? '-'
                          : `${row.pctVsTarget >= 0 ? '+' : ''}${(
                              row.pctVsTarget * 100
                            ).toFixed(1)}%`}
                      </TableCell>
                      <TableCell
                        className={
                          row.kilosAboveTargetPct == null
                            ? ''
                            : row.kilosAboveTargetPct >= 50
                              ? 'text-success-500'
                              : 'text-danger-500'
                        }
                      >
                        {row.kilosAboveTargetPct == null
                          ? '-'
                          : `${row.kilosAboveTargetPct}%`}
                      </TableCell>
                      <TableCell>
                        {row.q1Pct == null ? '-' : `${row.q1Pct}%`}
                      </TableCell>
                      <TableCell>
                        {row.q2Pct == null ? '-' : `${row.q2Pct}%`}
                      </TableCell>
                      <TableCell>
                        {row.q3Pct == null ? '-' : `${row.q3Pct}%`}
                      </TableCell>
                      <TableCell>
                        {row.q4Pct == null ? '-' : `${row.q4Pct}%`}
                      </TableCell>
                    </TableRow>
                  );
                }),
              ]}
            </TableBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default IndustryDimensionAnalysis;
